import { INormalizeBySetting } from "models";

import { EntityKind } from "./entityKind";
import { IBin, PerformanceDateRange, ShapefileMatchMode } from "./groupBy";
import { PdenSourceEnum } from "./pdenDataSourceSetting";
import { ForecastTrumpParamsType } from "./schemas/data.schema";

/** Well counts for a filter, from the filter service's /counts endpoint. */
export type FilterCountT = {
  Active?: number;
  "Header.WellType"?: number;
  "Header.Status"?: number;
  Polygon?: number;
  [key: string]: number;
};

export interface IQuery {
  operator: string;
  predicates: IPredicate[];
  children_nodes: IQuery[];
}

export default class FilterProperty {
  property: string;
  value: Array<string> | Array<number> | number | string;
  operator: string;

  constructor() {
    this.property = "";
    this.value = [];
    this.operator = "";
  }
}

export interface PolygonFilter {
  type: string;
  // Polygon: number[][][] ([ring][point][lng,lat]).
  // MultiPolygon (from refining a selection with intersecting lassos):
  // number[][][][] ([polygon][ring][point][lng,lat]).
  coordinates: number[][][] | number[][][][];
  id?: string;
  source?: string;
  operator?: string;
}

export interface MapScreenshotPolygon {
  coordinates: number[][][];
  id: string;
  type: string;
}

export interface WellListFilterModel {
  filterType: string;
  id: string;
  operator: string;
  type: string;
  value: string[];
  // Optional origin marker. Set to "xda" when the filter is applied from the XDA
  // toolbar so that toolbar can recognize its own filter without affecting the
  // shared filterType (which map-click selection keys off "well-list").
  source?: string;
}

export interface IFilterState {
  polygonFilter: PolygonFilter;
  excludePolygonsFilter: PolygonFilter[];
  wellListFilter: WellListFilterModel[];
  propertiesFilter: PropertyFilterModel[];
}

export interface IPredicateModel {
  id: string;
  title: string;
  displayTitle: string;
  count: number; //the number of wells that the predicate captures
  name: string;
  actualValue?: IPredicate | IQuery;
  expanded: boolean;
  operator: string;
  children: IPredicateModel[];
  type: string;
  isChecked: boolean;
}

export interface FilterModel {
  query: IQuery;
  previousFilterId: string;
  job_id: string;
  user_id: string;
  entityKind: EntityKind;
  scenarioIds: string[];
  /**
   * Redis TTL in minutes, honoured by eva-filter-svc (types/well_data.rs
   * `expireMinutes`). Absent means the service default, 12 h.
   */
  expireMinutes?: number;
}

export interface IPredicate {
  id?: string;
  type: string;
  property: string;
  categoryId?: number;
  canBin?: boolean;
  operator: string;
  value: string[] | number[] | string | number;
  bin?: IBin;
  source?: string;
  normalizeBySetting?: INormalizeBySetting;
  sourceKey?: string;
  // Set on the Others row's predicate: it names no value, so the service resolves the wells.
  rollup?: "Others";
  negatedOperator: boolean;
  groupByTitle?: string;
  partialContextTitle?: string;
  isForecastToggleOn?: boolean;
  selectedForecastFolderName?: string;
  forecastTrumpingSettings?: ForecastTrumpParamsType;
  performanceDateRange?: PerformanceDateRange;
  pdenSource: PdenSourceEnum;
  /**
   * Only set on predicates over a shapefile column, and only for the
   * non-default mode: an absent field means "intersecting". The predicate is
   * the one and only place a mode lives, so it keeps matching the way the user
   * built it whatever they focus on afterwards.
   */
  shapefileMatchMode?: ShapefileMatchMode;
  /**
   * How far outside the shapefile a well still counts, in metres. Only set on
   * predicates over a shapefile column, and only when there is one: an absent
   * field means the shapefile's own outline, which is what most filters are.
   */
  shapefileBufferMeters?: number;
  /**
   * The distance above as it was said when it was asked for, and the unit it
   * was said in. Written together or not at all, beside a buffer there is one
   * of, so the box and the chip can read back the number that was typed rather
   * than the round trip through metres — a thousand feet asked for reads a
   * thousand feet, not 984.25. Display only: nothing filters on them, and the
   * backend does not know they are there.
   */
  shapefileBufferDisplayValue?: number;
  shapefileBufferDisplayUnit?: string;
}

export interface PropertyFilterModel {
  predicates: IPredicate[];

  //where the property came from
  source: string;
}

export interface IPredicateType {
  Property: string;
  WellList: string;
  Polygon: string;
  MultiPolygon: string;
}

export interface IPredicateOperator {
  And: string;
  Or: string;
}

export interface IPredicatePolygonOperator {
  Equals: string;
  NotEqual: string;
}

export interface IPredicatePropertyOperator {
  Between: string;
  NotBetween: string;
  GreaterThan: string;
  GreaterThanOrEqual: string;
  Equals: string;
  NotEqual: string;
  LessThan: string;
  LessThanOrEqual: string;
  In: string;
  NotIn: string;
}

export interface IPropertyLegendFilter {
  legendFilters: PropertyFilterModel[];
  propertyName: string;
}

export type FilterItemT = {
  name: string;
  filterJSON: string;
  id: string;
  filterId?: string;
};

export const ActiveFilterItem: FilterItemT = {
  name: "Active Filter",
  filterJSON: "",
  id: "",
  filterId: ""
};

export enum FilterType {
  SystemDefault = "System",
  UserDefault = "User"
}

export enum FilterScope {
  System = "System",
  Org = "Org",
  Team = "Team",
  Personal = "Personal"
}

export interface SavedFilterModel {
  filterJSON: string;
  id: string;
  name: string;
  owner: string;
  projectID: string;
  entityKind?: EntityKind;
  filterType?: FilterType;
  filterScope?: FilterScope;
  projectName?: string;
}
